

// src/context/AuthContext.jsx
import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { onAuthStateChanged, signOut }           from "firebase/auth";
import { auth, db }                              from "../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  getCurrentSessionId,
  validateSession,
  updateSessionActivity,
  terminateSession
} from "../utils/sessionService";
import { logAuditEvent as logAudit, AUDIT_ACTIONS as AUDIT } from "../utils/auditService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Session activity update interval (5 minutes)
const SESSION_ACTIVITY_INTERVAL = 5 * 60 * 1000;
// Session validation check interval (1 minute) - checks if session expired
const SESSION_CHECK_INTERVAL = 60 * 1000;

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [businessId,   setBusinessId] = useState(null);
  const [sessionId,    setSessionId]  = useState(null);
  const [isAdmin,      setIsAdmin]    = useState(false);
  const [subscription, setSubscription] = useState({
    plan: 'free',
    status: 'inactive',
    features: [],
    trialEndsAt: null,
    currentPeriodEnd: null
  });
  const [loading,      setLoading]    = useState(true);
  const activityIntervalRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // read the users/{uid} document to get your businessId & isAdmin flag
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setBusinessId(data.businessId || null);
            setIsAdmin(   data.isAdmin    || false);

            // Load subscription data from business document
            // Only admins need subscription data; employees use their admin's subscription
            if (data.businessId && data.isAdmin) {
              try {
                const businessDoc = await getDoc(doc(db, "businesses", data.businessId));
                if (businessDoc.exists()) {
                  const businessData = businessDoc.data();

                  // Backfill the business timezone from the admin's browser if it
                  // was never set — Cloud Functions need this to format clock
                  // in/out notifications in local time instead of UTC.
                  if (!businessData.timezone) {
                    try {
                      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                      if (browserTz && browserTz !== "UTC") {
                        await updateDoc(doc(db, "businesses", data.businessId), {
                          timezone: browserTz,
                        });
                      }
                    } catch (tzErr) {
                      console.error("Error saving business timezone:", tzErr);
                    }
                  }

                  // Set subscription from business data
                  setSubscription({
                    plan: businessData.subscription?.plan || data.subscriptionPlan || 'free',
                    status: businessData.subscription?.status || data.subscriptionStatus || 'inactive',
                    features: businessData.subscription?.features || [],
                    trialEndsAt: data.trialEndsAt || null,
                    currentPeriodEnd: data.currentPeriodEnd || null,
                    seats: businessData.subscription?.seats || 1,
                    maxSeats: businessData.subscription?.maxSeats || 1,
                    billingCycle: businessData.subscription?.billingCycle || 'monthly',
                    stripeSubscriptionId: businessData.stripeSubscriptionId || null,
                    stripeCustomerId: data.stripeCustomerId || null
                  });
                } else {
                  // Business doc doesn't exist, use user-level data
                  setSubscription({
                    plan: data.subscriptionPlan || 'free',
                    status: data.subscriptionStatus || 'inactive',
                    features: [],
                    trialEndsAt: data.trialEndsAt || null,
                    currentPeriodEnd: data.currentPeriodEnd || null,
                    seats: 1,
                    maxSeats: 1,
                    billingCycle: 'monthly',
                    stripeSubscriptionId: null,
                    stripeCustomerId: data.stripeCustomerId || null
                  });
                }
              } catch (subErr) {
                console.error("Error loading subscription data:", subErr);
                // Set default free plan on error
                setSubscription({
                  plan: 'free',
                  status: 'inactive',
                  features: [],
                  trialEndsAt: null,
                  currentPeriodEnd: null,
                  seats: 1,
                  maxSeats: 1,
                  billingCycle: 'monthly',
                  stripeSubscriptionId: null,
                  stripeCustomerId: null
                });
              }
            } else if (data.businessId && !data.isAdmin) {
              // For employees, load the business subscription so they inherit feature access
              try {
                const businessDoc = await getDoc(doc(db, "businesses", data.businessId));
                if (businessDoc.exists()) {
                  const businessData = businessDoc.data();
                  setSubscription({
                    plan: businessData.subscription?.plan || 'free',
                    status: businessData.subscription?.status || 'inactive',
                    features: businessData.subscription?.features || [],
                    trialEndsAt: businessData.subscription?.trialEndsAt || null,
                    currentPeriodEnd: businessData.subscription?.currentPeriodEnd || null,
                    seats: businessData.subscription?.seats || 1,
                    maxSeats: businessData.subscription?.maxSeats || 1,
                    billingCycle: businessData.subscription?.billingCycle || 'monthly',
                    stripeSubscriptionId: null,
                    stripeCustomerId: null
                  });
                } else {
                  setSubscription({
                    plan: 'free',
                    status: 'inactive',
                    features: [],
                    trialEndsAt: null,
                    currentPeriodEnd: null,
                    seats: 1,
                    maxSeats: 1,
                    billingCycle: 'monthly',
                    stripeSubscriptionId: null,
                    stripeCustomerId: null
                  });
                }
              } catch (subErr) {
                console.error("Error loading business subscription for employee:", subErr);
                setSubscription({
                  plan: 'free',
                  status: 'inactive',
                  features: [],
                  trialEndsAt: null,
                  currentPeriodEnd: null,
                  seats: 1,
                  maxSeats: 1,
                  billingCycle: 'monthly',
                  stripeSubscriptionId: null,
                  stripeCustomerId: null
                });
              }
            }

            // Validate and track session
            const storedSessionId = getCurrentSessionId();
            if (data.businessId && storedSessionId) {
              const isValidSession = await validateSession(data.businessId, storedSessionId);
              if (isValidSession) {
                setSessionId(storedSessionId);
                // Update session activity
                await updateSessionActivity(data.businessId, storedSessionId);
              } else {
                // Session is invalid/expired - clear it but don't force logout
                // The session was likely terminated from another device or expired
                console.warn("Session invalid or expired");
                setSessionId(null);
              }
            }

            // you can merge any other fields you store
            setCurrentUser({ uid: user.uid, email: user.email, sessionId: storedSessionId, ...data });
          } else {
            // fallback if no Firestore doc
            setCurrentUser(user);
            setBusinessId(null);
            setIsAdmin(false);
            setSubscription({
              plan: 'free',
              status: 'inactive',
              features: [],
              trialEndsAt: null,
              currentPeriodEnd: null,
              seats: 1,
              maxSeats: 1,
              billingCycle: 'monthly',
              stripeSubscriptionId: null,
              stripeCustomerId: null
            });
          }
        } catch (err) {
          console.error("Error loading user data:", err);
          // still set currentUser so the app can decide what to do
          setCurrentUser(user);
          setBusinessId(null);
          setIsAdmin(false);
          setSubscription({
            plan: 'free',
            status: 'inactive',
            features: [],
            trialEndsAt: null,
            currentPeriodEnd: null,
            seats: 1,
            maxSeats: 1,
            billingCycle: 'monthly',
            stripeSubscriptionId: null,
            stripeCustomerId: null
          });
        }
      } else {
        // signed out
        setCurrentUser(null);
        setBusinessId(null);
        setIsAdmin(false);
        setSubscription({
          plan: 'free',
          status: 'inactive',
          features: [],
          trialEndsAt: null,
          currentPeriodEnd: null,
          seats: 1,
          maxSeats: 1,
          billingCycle: 'monthly',
          stripeSubscriptionId: null,
          stripeCustomerId: null
        });
      }

      setLoading(false);
    });

    return unsub;
  }, []);

  // Session activity heartbeat - updates lastActive every 5 minutes
  useEffect(() => {
    if (!businessId || !sessionId) {
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
        activityIntervalRef.current = null;
      }
      return;
    }

    // Update activity immediately
    updateSessionActivity(businessId, sessionId).catch(console.warn);

    // Set up interval for periodic updates
    activityIntervalRef.current = setInterval(() => {
      updateSessionActivity(businessId, sessionId).catch(console.warn);
    }, SESSION_ACTIVITY_INTERVAL);

    return () => {
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
        activityIntervalRef.current = null;
      }
    };
  }, [businessId, sessionId]);

  // Session expiration check - auto logout when session times out (security feature)
  useEffect(() => {
    if (!businessId || !sessionId) {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      return;
    }

    const checkSessionExpiration = async () => {
      try {
        const isValid = await validateSession(businessId, sessionId);
        if (!isValid) {
          console.warn("Session expired - logging out for security");
          // Session expired or was terminated - force logout
          await signOut(auth).catch(console.warn);
          localStorage.removeItem("effitrack_session_id");
          window.location.href = "/login?reason=session_expired";
        }
      } catch (error) {
        console.error("Session check failed:", error);
      }
    };

    // Check session validity every minute
    sessionCheckIntervalRef.current = setInterval(checkSessionExpiration, SESSION_CHECK_INTERVAL);

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    };
  }, [businessId, sessionId]);

  // Logout function with session termination
  const logout = useCallback(async () => {
    try {
      const currentSessionId = sessionId || getCurrentSessionId();

      // Update last active timestamp
      if (currentUser?.uid) {
        await updateDoc(doc(db, "users", currentUser.uid), {
          lastActive: serverTimestamp(),
          status: "offline"
        }).catch(console.warn);
      }

      // Terminate the session
      if (businessId && currentSessionId) {
        await terminateSession(businessId, currentSessionId, "user_logout");

        // Log audit event
        await logAudit(businessId, AUDIT.LOGOUT, currentUser?.uid, currentUser?.email, {
          sessionId: currentSessionId
        }).catch(console.warn);
      }

      // Clear local state
      setSessionId(null);

      // Sign out from Firebase
      await signOut(auth);

      // Redirect to login
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
      // Force sign out even if session termination fails
      await signOut(auth).catch(console.warn);
      window.location.href = "/login";
    }
  }, [businessId, sessionId, currentUser]);

  // Allow Login.jsx (or others) to re-read subscription after docs are created
  const refreshSubscription = useCallback(async (bizId) => {
    const targetBizId = bizId || businessId;
    if (!targetBizId) return;
    try {
      const bizDoc = await getDoc(doc(db, "businesses", targetBizId));
      if (bizDoc.exists()) {
        const bd = bizDoc.data();
        const uid = auth.currentUser?.uid;
        const userSnap = uid ? await getDoc(doc(db, "users", uid)) : null;
        const ud = userSnap?.data() || {};
        setBusinessId(targetBizId);
        setIsAdmin(ud.isAdmin || false);
        setSubscription({
          plan: bd.subscription?.plan || ud.subscriptionPlan || 'free',
          status: bd.subscription?.status || ud.subscriptionStatus || 'inactive',
          features: bd.subscription?.features || [],
          trialEndsAt: ud.trialEndsAt || bd.trialEndsAt || null,
          currentPeriodEnd: ud.currentPeriodEnd || null,
          seats: bd.subscription?.seats || 1,
          maxSeats: bd.subscription?.maxSeats || 1,
          billingCycle: bd.subscription?.billingCycle || 'monthly',
          stripeSubscriptionId: bd.stripeSubscriptionId || null,
          stripeCustomerId: ud.stripeCustomerId || null,
        });
      }
    } catch (err) {
      console.error("Error refreshing subscription:", err);
    }
  }, [businessId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      currentUser,
      businessId,
      sessionId,
      isAdmin,
      subscription,
      refreshSubscription,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}
